import React, { useState, useCallback } from 'react';
import * as R from 'ramda';
import { injectStripe } from 'react-stripe-elements';
import styled, { css, withTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Flex, Box, Input, Button, Notification } from '@cowsquare/design';
import { CardNumberElement, CardExpiryElement, CardCVCElement } from 'react-stripe-elements';
import { chargeForEvent } from '../utils/chargeForEvent';
import { updateEventQuery } from '../utils/updateEventQuery';
import { navigate } from 'gatsby';

const EVENT_NAME_MAP = {
  cme_events: 'cme',
  e_drug_events: 'e-pharm',
  hkdu_events: 'member-only',
};

const getErrorCode = R.pipe(R.pathOr('', ['error', 'code']));

const getInputStyle = props => ({
  base: {
    color: 'black',
    fontSize: themeGet('input.fontSize')(props),
    fontSmoothing: 'antialiased',
    '::placeholder': {
      color: themeGet('input.placeHolderColor')(props),
    },
  },
});

const RowLabel = styled(Box).attrs({
  mb: 2,
})`
  display: inline-block;
  font-size: ${themeGet('form.label.fontSize')};
  line-height: 1.43;
  color: ${themeGet('form.label.color')};
`;

const RowValue = styled.div`
  .StripeElement {
    padding: 8px 10px;
    height: 32px;
    border-radius: ${themeGet('input.borderRadius')};
    line-height: 1.43;
    background-color: ${themeGet('input.backgroundColor')};

    &:focus-within {
      ${props =>
        !props.error &&
        css`
          border-top: ${themeGet('input.focus.border.top')};
          border-right: ${themeGet('input.focus.border.right')};
          border-bottom: ${themeGet('input.focus.border.bottom')};
          border-left: ${themeGet('input.focus.border.left')};
          background-color: ${themeGet('input.focus.backgroundColor')};
          outline: none;
        `}
    }

    &:hover {
      ${props =>
        !props.error &&
        css`
          background-color: ${themeGet('input.hover.backgroundColor')};
        `}
    }

    ${props =>
      props.error &&
      css`
        border-top: ${themeGet('input.error.border.top')};
        border-right: ${themeGet('input.error.border.right')};
        border-bottom: ${themeGet('input.error.border.bottom')};
        border-left: ${themeGet('input.error.border.left')};
        background-color: ${themeGet('input.error.backgroundColor')};
      `}
  }

  .StripeElement--focus {
    ${props =>
      !props.error &&
      css`
        border-top: ${themeGet('input.focus.border.top')};
        border-right: ${themeGet('input.focus.border.right')};
        border-bottom: ${themeGet('input.focus.border.bottom')};
        border-left: ${themeGet('input.focus.border.left')};
        background-color: ${themeGet('input.focus.backgroundColor')};
        outline: none;
      `}
  }
`;

const Message = styled(Box).attrs({
  mt: 3,
})`
  color: #ff4848;
  font-size: 13px;
  line-height: 1.54;
  min-height: 20px;
  visibility: visible;
`;

const Field = ({ label, value, mr = 0, errorMessage }) => (
  <Box mr={mr} width="100%">
    <RowLabel>{label}</RowLabel>
    <RowValue error={!!errorMessage}>{value}</RowValue>
    <Message>{errorMessage && 'Invalid format'}</Message>
  </Box>
);

const CheckoutSection = ({ enrolled, doctorId, eventId, eventType, stripe, theme }) => {
  const [errorMessage, setErrorMessage] = useState({});
  const [name, setName] = useState('');
  const [isPaying, setIsPaying] = useState(false);
  const validateName = useCallback(name => {
    setErrorMessage(state => ({
      ...state,
      nameError: !name && 'name is required',
    }));
  }, []);
  const handleName = useCallback(event => {
    const currentName = event.target.value;
    if (currentName) {
      setErrorMessage(state => ({
        ...state,
        nameError: null,
      }));
    }
    setName(currentName);
  }, []);

  return (
    <>
      <Field
        label="Name on Card"
        value={
          <Input
            type="text"
            placeholder="Name"
            value={name}
            onChange={handleName}
            error={errorMessage.nameError}
          />
        }
        errorMessage={errorMessage.nameError}
      />
      <Field
        label="Card Number"
        value={
          <CardNumberElement
            style={getInputStyle({ theme })}
            placeholder="Please enter card number without - or spaces"
            onChange={event =>
              setErrorMessage(state => ({
                ...state,
                numberError: getErrorCode(event),
              }))
            }
          />
        }
        errorMessage={errorMessage.numberError}
      />
      <Flex flexDirection="row">
        <Field
          label="Expiration"
          value={
            <CardExpiryElement
              style={getInputStyle({ theme })}
              placeholder="MM/YY"
              onChange={event =>
                setErrorMessage(state => ({
                  ...state,
                  expiryError: getErrorCode(event),
                }))
              }
            />
          }
          mr={5}
          errorMessage={errorMessage.expiryError}
        />
        <Field
          label="CVC"
          value={
            <CardCVCElement
              style={getInputStyle({ theme })}
              placeholder="Security code"
              onChange={event =>
                setErrorMessage(state => ({
                  ...state,
                  cvcError: getErrorCode(event),
                }))
              }
            />
          }
          errorMessage={errorMessage.cvcError}
        />
      </Flex>
      <Flex justifyContent="flex-end">
        <Button
          variant="primary"
          disabled={enrolled || isPaying}
          onClick={() => {
            validateName(name);
            stripe
              .createToken({
                type: 'card',
                owner: {
                  name,
                },
              })
              .then(({ token }) => {
                if (name && token) {
                  setIsPaying(true);
                  return chargeForEvent(doctorId, eventId, eventType, token.id)
                    .then(result => {
                      navigate(`/${EVENT_NAME_MAP[eventType]}/events/${eventId}`);
                      Notification.success({
                        message: 'Success!',
                        description: R.pathOr('', ['data', 'message'])(result),
                      });
                    })
                    .catch(err => {
                      setIsPaying(false);
                      return Notification.error({
                        message: 'Failed!',
                        description: R.pathOr('', ['response', 'data', 'message'])(err),
                      });
                    })
                    .then(updateEventQuery(eventType, eventId, doctorId));
                }
              });
          }}
        >
          Pay Now
        </Button>
      </Flex>
    </>
  );
};

export default injectStripe(withTheme(CheckoutSection));
